import {
  MessageBar,
  MessageBarBody,
  makeStyles,
  Spinner,
  tokens,
} from "@fluentui/react-components";
import {
  AddCircleRegular,
  BoxMultipleRegular,
  BuildingShopRegular,
  FormNewRegular,
  ScanTextRegular,
  TagMultipleRegular,
  TreeDeciduousRegular,
} from "@fluentui/react-icons";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { ApiError, auth, loadSession } from "./api/client";
import { SignInScreen } from "./auth/SignInScreen";
import { AddPiecePage } from "./catalogue/AddPiecePage";
import { LocationsPage } from "./locations/LocationsPage";
import { AttributeSettingsPage } from "./settings/AttributeSettingsPage";
import { CategoriesPage } from "./settings/CategoriesPage";
import { SerialFormatPage } from "./settings/SerialFormatPage";
import { AppShell, type NavItem } from "./shell/AppShell";
import { GoodsInPage } from "./stock/GoodsInPage";
import { StockLookupPage } from "./stock/StockLookupPage";
import { StockOnHandPage } from "./stock/StockOnHandPage";
import { useScheme } from "./theme/ColourSchemeProvider";
import { EmptyState } from "./ui/PageHeader";

const useStyles = makeStyles({
  centre: {
    minHeight: "100vh",
    display: "grid",
    placeItems: "center",
    backgroundColor: tokens.colorNeutralBackground2,
  },
});

type Page =
  | "add-piece"
  | "on-hand"
  | "goods-in"
  | "stock"
  | "locations"
  | "categories"
  | "fields"
  | "serials";

/**
 * The destinations, in the order the work actually happens: add a piece, see what you have, then
 * the fuller warehouse tools, then the settings an owner visits once a month.
 *
 * `primary` is what keeps a slot on a phone's bottom bar. The four marked here are the four a
 * shop uses daily; the settings screens sit behind "More", which is the right cost for something
 * opened rarely and the wrong cost for something opened hourly.
 */
const PAGES: NavItem<Page>[] = [
  {
    value: "add-piece",
    label: "Add a piece",
    icon: <AddCircleRegular />,
    permission: "product.create",
    primary: true,
  },
  {
    value: "on-hand",
    label: "Stock",
    icon: <BoxMultipleRegular />,
    permission: "inventory.read",
    primary: true,
  },
  {
    value: "goods-in",
    label: "Goods in",
    icon: <BuildingShopRegular />,
    permission: "inventory.receive",
    primary: true,
  },
  {
    value: "stock",
    label: "Find a unit",
    icon: <ScanTextRegular />,
    permission: "inventory.read",
    primary: true,
  },
  {
    value: "locations",
    label: "Locations",
    icon: <BuildingShopRegular />,
    permission: "inventory.read",
  },
  // Categories before Product fields: the owner sets up the tree first, then hangs questions
  // off it.
  {
    value: "categories",
    label: "Categories",
    icon: <TreeDeciduousRegular />,
    permission: "category.manage",
  },
  {
    value: "fields",
    label: "Product fields",
    icon: <FormNewRegular />,
    permission: "settings.manage",
  },
  {
    value: "serials",
    label: "Serials & labels",
    icon: <TagMultipleRegular />,
    permission: "settings.manage",
  },
];

export function App() {
  const styles = useStyles();
  const queryClient = useQueryClient();
  const { isDark, choose } = useScheme();

  const [hasSession, setHasSession] = useState(() => loadSession() !== null);
  const [page, setPage] = useState<Page | null>(null);

  const {
    data: user,
    isLoading,
    error,
    refetch,
  } = useQuery({ queryKey: ["me"], queryFn: auth.me, enabled: hasSession, retry: false });

  const onSignedIn = useCallback(() => {
    setHasSession(true);
    void queryClient.invalidateQueries({ queryKey: ["me"] });
    void refetch();
  }, [queryClient, refetch]);

  // A disabled query reports isPending: true, so the session check has to come first.
  if (!hasSession) {
    return <SignInScreen onSignedIn={onSignedIn} />;
  }

  if (isLoading) {
    return (
      <div className={styles.centre}>
        <Spinner label="Loading your workspace" />
      </div>
    );
  }

  if (!user) {
    return <SignInScreen onSignedIn={onSignedIn} />;
  }

  const signOut = async () => {
    await auth.signOut();
    queryClient.clear();
    window.location.reload();
  };

  // The navigation renders from the permissions the API returned, not from a list held here.
  const visible = PAGES.filter((p) => user.permissions.includes(p.permission));

  // Nothing is selected until the permissions are known, so a stock-taker whose only page is
  // Goods in does not land on an empty tab.
  const current =
    page && visible.some((p) => p.value === page) ? page : (visible[0]?.value ?? null);

  return (
    <AppShell<Page>
      items={visible}
      current={current}
      onNavigate={setPage}
      tenantLabel={user.tenantId ? "Filora" : "BAE Platform"}
      isPlatform={user.tenantId === null}
      displayName={user.displayName}
      email={user.email}
      isDark={isDark}
      onToggleTheme={() => choose(isDark ? "light" : "dark")}
      onSignOut={() => void signOut()}
    >
      {error instanceof ApiError && (
        <MessageBar intent="error">
          <MessageBarBody>{error.message}</MessageBarBody>
        </MessageBar>
      )}

      {current === "add-piece" && <AddPiecePage />}
      {current === "on-hand" && <StockOnHandPage />}
      {current === "goods-in" && <GoodsInPage />}
      {current === "stock" && <StockLookupPage />}
      {current === "locations" && <LocationsPage />}
      {current === "categories" && <CategoriesPage />}
      {current === "fields" && <AttributeSettingsPage />}
      {current === "serials" && <SerialFormatPage />}
      {current === null && (
        <EmptyState icon={<BoxMultipleRegular />} title="Your account has no pages yet">
          Ask whoever set this up to give you a role.
        </EmptyState>
      )}
    </AppShell>
  );
}
